import { isState, forActions } from "@snap/state-management";
import { Injectable } from "@snap/ts-inject";
import { TypedCustomEvent } from "../events/TypedCustomEvent";
import type { TypedEventListener, TypedEventListenerOptions } from "../events/TypedEventTarget";
import { TypedEventTarget } from "../events/TypedEventTarget";
import type { UriHandler, UriResponse } from "../uri-handlers/UriHandlers";
import type { Lens } from "../lens/Lens";
import { getLogger } from "../logger/logger";
import type { LensState } from "./lensState";
import { lensStateFactory } from "./lensState";

const logger = getLogger("LensKeyboard");

/**
 * "active" event payload.
 *
 * This event is emitted when a lens requests a keyboard to be shown or dismissed.
 * The application should listen for this event and display or remove its keyboard UI accordingly.
 */
export interface KeyboardActiveEvent {
    /**
     * Indicates whether the keyboard should be displayed (`true`) or hidden (`false`).
     */
    active: boolean;

    /**
     * The current text input associated with the keyboard.
     */
    text: string;

    /**
     * The lens that initiated the keyboard request.
     */
    lens: Lens;

    /**
     * @deprecated Clients apps are responsible to create keyboard UI.
     */
    element: HTMLTextAreaElement;
}

/**
 * Defines events emitted by the `Keyboard` API.
 */
export type KeyboardEvents = TypedCustomEvent<"active", KeyboardActiveEvent>;

/**
 * Listener of {@link KeyboardEvents} events.
 */
export type KeyboardEventListener = TypedEventListener<KeyboardEvents>;

/**
 * The `Keyboard` API enables applications to handle text input requests triggered by lenses.
 *
 * Lenses can request a keyboard display by emitting an `active` event. The application is responsible for displaying
 * a keyboard UI in response to this event.
 *
 * As users type, the application sends the typed text back to the lens using {@link Keyboard.sendInputToLens},
 * allowing the lens to display the text accordingly.
 *
 * Lenses can also request the keyboard to be dismissed. When this happens, the application receives an event
 * and should remove the displayed keyboard UI.
 *
 * @example
 * ```ts
 * cameraKitSession.keyboard.addEventListener('active', ({ detail }) => {
 *   const { active, text } = detail;
 *   if (active) {
 *     showKeyboard(text, (newText) => cameraKitSession.keyboard.sendInputToLens(newText));
 *   } else {
 *     hideKeyboard();
 *   }
 * });
 * ```
 *
 * @category Lenses
 */
export interface Keyboard {
    /**
     * Adds an event listener for keyboard-related events.
     *
     * @param type - The type of event to listen for (e.g., "active").
     * @param callback - Function that handles the event.
     * @param options - Additional options for event listener behavior (optional).
     */
    addEventListener: (type: "active", callback: KeyboardEventListener, options?: TypedEventListenerOptions) => void;

    /**
     * Removes a previously added event listener.
     *
     * @param type - The type of event to remove.
     * @param callback - The event listener function to remove.
     */
    removeEventListener: (type: "active", callback: KeyboardEventListener) => void;

    /**
     * Sends a string of text to the active lens. The application should use this method
     * to provide user-typed input to the lens for display.
     *
     * @param text - The text to send to the lens. Supports escape sequences (e.g., `\n` for multiline input).
     */
    sendInputToLens: (text: string) => void;

    /**
     * Dismisses the keyboard, clears the text input, and triggers an `active` event with `active: false`.
     * Applications can use this to remove on-screen text input elements when input is no longer needed.
     */
    dismiss: () => void;

    /**
     * @deprecated Clients apps are responsible to create keyboard UI.
     */
    getElement: () => HTMLTextAreaElement;
}

// eslint-disable-next-line max-len
// https://github.sc-corp.net/Snapchat/LensCore/blob/4c05e721d0321133d50148e5b37fea5078516abd/Src/Scenarium/Infrastructure/TextInputSystem.Scn.hpp#L38
type KeyboardType = "Text" | "Number" | "Phone" | "Url";

// eslint-disable-next-line max-len
// https://github.sc-corp.net/Snapchat/LensCore/blob/4c05e721d0321133d50148e5b37fea5078516abd/Src/Scenarium/Infrastructure/TextInputSystem.Scn.hpp#L44
type ReturnKeyType = "Done" | "Go" | "Next" | "Return" | "Search" | "Send";

interface RequestKeyboardData {
    enablePreview: boolean;
    end: number;
    keyboardType: KeyboardType;
    returnKeyType: ReturnKeyType;
    start: number;
    text: string;
}

interface DismissReply {
    keyboardOpen: false;
}

interface SetTextReply {
    text: string;
    start: number;
    end: number;
    done: boolean;
    shouldNotify: boolean;
}

/** @internal */
export class LensKeyboard {
    public readonly uriHandler: UriHandler;
    private readonly events: TypedEventTarget<KeyboardEvents>;
    private readonly element: HTMLTextAreaElement;
    private active = false;
    private text: string | undefined = undefined;
    private reply: (response: UriResponse) => void = () => {};

    constructor(private readonly lensState: LensState) {
        this.onElementKeyPress = this.onElementKeyPress.bind(this);
        this.events = new TypedEventTarget<KeyboardEvents>();
        this.element = document.createElement("textarea");
        this.element.addEventListener("keypress", this.onElementKeyPress);
        this.uriHandler = {
            uri: "app://textInput/",
            handleRequest: (request, reply) => {
                if (request.uri === "app://textInput/requestKeyboard") {
                    const data = JSON.parse(new TextDecoder().decode(request.data)) as RequestKeyboardData;
                    this.element.value = data.text;
                    this.text = data.text;
                    this.reply = reply;
                    this.active = true;
                    this.notifyClient();
                    this.element.focus();
                } else if (request.uri === "app://textInput/dismissKeyboard") {
                    this.active = false;
                    this.sendReply({ keyboardOpen: false });
                    this.notifyClient();
                } else {
                    logger.error(new Error(`Unhandled lens keyboard request '${request.uri}'.`));
                }
            },
        };
        lensState.events.pipe(forActions("turnedOff")).subscribe(() => {
            this.dismiss();
        });
    }

    dismiss(): void {
        this.active = false;
        this.element.value = "";
        this.text = "";
        this.sendReply({ keyboardOpen: false });
        this.notifyClient();
    }

    getElement(): HTMLTextAreaElement {
        return this.element;
    }

    sendInputToLens(text: string): void {
        this.element.value = text;
        this.text = text;
        this.sendReply({
            text: text,
            start: text.length,
            end: text.length,
            done: true,
            shouldNotify: true,
        });
    }

    addEventListener(type: "active", callback: KeyboardEventListener, options?: TypedEventListenerOptions): void {
        this.events.addEventListener(type, callback, options);
    }

    removeEventListener(type: "active", callback: KeyboardEventListener): void {
        this.events.removeEventListener(type, callback);
    }

    toPublicInterface(): Keyboard {
        return {
            addEventListener: this.addEventListener.bind(this),
            removeEventListener: this.removeEventListener.bind(this),
            sendInputToLens: this.sendInputToLens.bind(this),
            dismiss: this.dismiss.bind(this),
            getElement: this.getElement.bind(this),
        };
    }

    destroy() {
        // TODO: remove when removing deprecated element ref
        this.element.removeEventListener("keypress", this.onElementKeyPress);
    }

    private sendReply(data: DismissReply | SetTextReply) {
        this.reply({
            code: 200,
            description: "",
            contentType: "application/json",
            data: new TextEncoder().encode(JSON.stringify(data)),
        });
    }

    private notifyClient(): void {
        const state = this.lensState.getState();
        // If lens keyboard status is changing, we know a lens must be applied.
        if (isState(state, "noLensApplied")) return;
        this.events.dispatchEvent(
            new TypedCustomEvent("active", {
                element: this.element,
                active: this.active,
                text: this.text ?? "",
                // If the keyboard is up, it has been triggered by an active lens.
                lens: state.data,
            })
        );
    }

    private onElementKeyPress(event: KeyboardEvent) {
        if (event.code === "Enter" && !event.shiftKey) {
            event.preventDefault();
            this.sendInputToLens(this.element.value);
        }
    }
}

/**
 * Factory function for creating a LensKeyboard instance.
 *
 * @internal
 */
export const lensKeyboardFactory = Injectable(
    "lensKeyboard",
    [lensStateFactory.token] as const,
    (lensState: LensState) => new LensKeyboard(lensState)
);
